import { useEffect, useState } from 'react';
import Modal from 'react-modal';

import LoadingIndicator from '@components/LoadingIndicator';
import Playlist, { PlaylistAttributes } from '@data/interfaces/playlist.interface';
import { useChurchDataProvider } from '@data/ChurchDataProvider';

import closeIcon from 'url:@icon/close.svg';
import checkmarkIcon from 'url:@icon/checkmark.svg';

import './index.scss';

// Creates a playlist, or edits one when `playlist` is given. The caller does
// the saving, so the same form serves both.
export default function AddPlaylistModal(props: {
  isOpen: boolean,
  setIsOpen: (isOpen: boolean) => void,
  playlist?: Playlist | null,
  onSave: (attributes: PlaylistAttributes) => Promise<{ error?: string; playlist?: Playlist | null; }>
}) {
  const isEditing = Boolean(props.playlist);
  const churchData = useChurchDataProvider();

  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [campusID, setCampusID] = useState('');
  const [categoryID, setCategoryID] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Each open starts from the playlist being edited, or blank for a new one.
  useEffect(() => {
    if (props.isOpen) {
      setName(props.playlist?.attributes.name ?? '');
      setCampusID(props.playlist?.attributes.campusID ?? '');
      setCategoryID(props.playlist?.attributes.categoryID ?? '');
      setError(null);
      setSaving(false);
    }
  }, [props.isOpen, props.playlist]);

  const submit = async () => {
    if (saving || name.trim().length === 0) {
      return;
    }

    setSaving(true);
    setError(null);

    const response = await props.onSave({
      name: name.trim(),
      campusID: campusID || null,
      categoryID: categoryID || null
    });

    if (response.error) {
      setError(response.error);
      setSaving(false);
      return;
    }

    props.setIsOpen(false);
  };

  return <Modal
    isOpen={props.isOpen}
    id='add-playlist-modal'
    closeTimeoutMS={200}
    className='modal add-playlist-modal-shell'
  >
    <div className='add-playlist-modal'>
      <div className='add-playlist-modal-header'>
        <button
          type='button'
          className='add-playlist-modal-header-control close'
          disabled={saving}
          onClick={() => {
            props.setIsOpen(false);
          }}>
          <img src={closeIcon} alt='Close icon' />
        </button>

        <h3>{isEditing ? 'Edit Playlist' : 'New Playlist'}</h3>

        <button
          type='button'
          className='add-playlist-modal-header-control confirm'
          disabled={saving || name.trim().length === 0}
          onClick={() => {
            void submit();
          }}>
          <img src={checkmarkIcon} alt='Confirm icon' />
        </button>
      </div>

      <div className='add-playlist-modal-body'>
        {saving ? <div className='add-playlist-modal-loading-state'>
          <LoadingIndicator fullScreen={false} />
        </div> : <>
          {error ? <p className='add-playlist-modal-error'>{error}</p> : null}

          <div className='field-container'>
            <label htmlFor='playlist-name'>Name</label>
            <input
              id='playlist-name'
              type='text'
              value={name}
              placeholder='Popular Worship Songs'
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className='field-container'>
            <label htmlFor='playlist-campus'>Campus</label>
            <select
              id='playlist-campus'
              value={campusID}
              onChange={(e) => setCampusID(e.target.value)}>
              <option value=''>No campus</option>
              {churchData.campuses.map((campus) => (
                <option key={campus.id} value={campus.id}>
                  {campus.attributes.name}
                </option>
              ))}
            </select>
          </div>

          <div className='field-container'>
            <label htmlFor='playlist-category'>Category</label>
            <select
              id='playlist-category'
              value={categoryID}
              onChange={(e) => setCategoryID(e.target.value)}>
              <option value=''>No category</option>
              {churchData.categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
        </>}
      </div>
    </div>
  </Modal>
}
